// UsLocationsIndexPage — index of every US city & state we have pages for.
// Route: /us/locations

import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, MapPin, Building2, Map as MapIcon } from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import PageHero from "../components/shared/PageHero";
import CTABanner from "../components/homepage/CTABanner";
import { US_CITIES, US_STATES } from "../data/usLocations";
import { US_KEYWORDS } from "../data/usKeywords";

function LocationCard({ l, idx }) {
  const emoji = l.emoji || (l.type === "city" ? "🏙️" : "🗺️");
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.3, delay: Math.min(idx * 0.03, 0.2) }}>
      <Link
        to={`/us/${l.slug}`}
        className="group rounded-2xl border border-zinc-100 bg-white p-6 flex flex-col h-full shadow-card hover:shadow-card-hover hover:border-electric/30 hover:-translate-y-1 transition-all"
        data-testid={`loc-card-${l.slug}`}
      >
        <div className="flex items-center justify-between mb-4">
          <span
            className="w-12 h-12 grid place-items-center rounded-xl bg-electric/8 border border-electric/15 text-2xl leading-none group-hover:scale-110 transition-transform"
            aria-hidden
          >
            {emoji}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-zinc-400">{l.type === "city" ? l.stateAbbr : "State"}</span>
        </div>
        <h3 className="font-display text-lg font-bold text-charcoal group-hover:text-electric leading-snug transition-colors">{l.name}</h3>
        <p className="mt-1 text-sm text-zinc-500 leading-relaxed flex-1">{l.tagline}</p>
        <p className="mt-4 font-display text-xl font-extrabold text-charcoal">{l.stat.value}</p>
        <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-zinc-400 group-hover:text-electric uppercase tracking-widest transition-colors">
          {US_KEYWORDS.length} services <ArrowUpRight className="w-3.5 h-3.5" />
        </span>
      </Link>
    </motion.div>
  );
}

export default function UsLocationsIndexPage() {
  return (
    <>
      <Helmet>
        <title>Digital Marketing Agency by City & State — USA | Lumotis</title>
        <meta name="description" content={`Lumotis serves businesses across ${US_CITIES.length} major US cities and ${US_STATES.length} states with SEO, ads, ecommerce, web design and AI marketing. Find your local page.`} />
        <link rel="canonical" href="https://lumotis.com/us/locations" />
        <meta property="og:title" content="Digital Marketing Agency by City & State — USA | Lumotis" />
        <meta property="og:url" content="https://lumotis.com/us/locations" />
        <meta property="og:type" content="website" />
      </Helmet>

      <Navbar />

      <PageHero
        eyebrow="🇺🇸 USA Locations"
        title="Digital marketing, localised to"
        titleAccent="your city and state."
        subtitle={`We serve businesses in ${US_CITIES.length} major US cities and ${US_STATES.length} states — with ${US_KEYWORDS.length} specialised services in each. Pick your location to see what we do there.`}
        breadcrumb={[{ label: "Home", href: "/" }, { label: "USA", href: "/us" }, { label: "Locations" }]}
        primaryCta={{ to: "/seo-audit", label: "Get a Free Audit", testid: "locations-cta" }}
        secondaryCta={{ to: "/contact", label: "Talk to us" }}
      />

      <section className="py-16 md:py-20 bg-white">
        <div className="container">
          <div className="flex items-center gap-3 mb-8">
            <Building2 className="w-5 h-5 text-electric" />
            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-charcoal">Top {US_CITIES.length} cities</h2>
            <span className="flex-1 h-px bg-zinc-100" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {US_CITIES.map((l, i) => <LocationCard key={l.slug} l={l} idx={i} />)}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-zinc-50 border-y border-zinc-100">
        <div className="container">
          <div className="flex items-center gap-3 mb-8">
            <MapIcon className="w-5 h-5 text-electric" />
            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-charcoal">Top {US_STATES.length} states</h2>
            <span className="flex-1 h-px bg-zinc-200" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {US_STATES.map((l, i) => <LocationCard key={l.slug} l={l} idx={i} />)}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="container rounded-2xl border border-zinc-100 bg-zinc-50 p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl font-bold text-charcoal flex items-center gap-2"><MapPin className="w-5 h-5 text-electric" /> Don't see your city?</h3>
            <p className="mt-2 text-zinc-500 leading-relaxed max-w-xl">We work with businesses anywhere in the US. Tell us where you are and what you need — we'll localise a strategy for your market.</p>
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold rounded-xl hover:bg-electric-500 transition-colors shadow-lg shadow-electric/25 w-fit shrink-0">
            Talk to us <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <CTABanner />
      <Footer />
    </>
  );
}
